// KnowledgeBase.js - Knowledge Base Articles Management

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, Chip, IconButton, Card, CardContent,
  CardActions, TextField, InputAdornment, MenuItem, Select, FormControl,
  InputLabel, CircularProgress, Alert, Tabs, Tab, List, ListItem, ListItemButton,
  ListItemText, Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Folder as FolderIcon,
  Article as ArticleIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../utils/permissionUtils';
import { articleService, articleCategoryService } from './helpDeskService';
import {
  ArticleStatusLabels, ArticleStatusColors, ArticleStatusOptions,
  ArticleVisibilityLabels, ArticleVisibilityOptions,
  formatDate, truncateText
} from './helpDeskEnums';
import ArticleForm from './ArticleForm';

const KnowledgeBase = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('');

  // Form states
  const [formOpen, setFormOpen] = useState(searchParams.get('new') === 'true');
  const [editArticle, setEditArticle] = useState(null);
  const [viewArticle, setViewArticle] = useState(null);

  useEffect(() => {
    document.title = 'Knowledge Base | Help Desk';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadData();
    }
  }, [canView, chatbotId, searchTerm, selectedCategory, statusFilter, visibilityFilter]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        chatbot_id: chatbotId,
        search: searchTerm || undefined,
        category_id: selectedCategory || undefined,
        status: statusFilter || undefined,
        visibility: visibilityFilter || undefined
      };

      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const [articlesRes, categoriesRes] = await Promise.allSettled([
        articleService.getAll(params),
        articleCategoryService.getAll({ chatbot_id: chatbotId })
      ]);

      if (articlesRes.status === 'fulfilled' && articlesRes.value.success) {
        setArticles(articlesRes.value.data || []);
      }

      if (categoriesRes.status === 'fulfilled' && categoriesRes.value.success) {
        setCategories(categoriesRes.value.data || []);
      }
    } catch (err) {
      enqueueSnackbar('Failed to load articles', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (article = null) => {
    setEditArticle(article);
    setFormOpen(true);
  };

  const handleCloseForm = (refresh = false) => {
    setFormOpen(false);
    setEditArticle(null);
    searchParams.delete('new');
    setSearchParams(searchParams);
    if (refresh) {
      loadData();
    }
  };

  const handleViewArticle = async (article) => {
    try {
      const response = await articleService.getById(article.id);
      if (response.success) {
        setViewArticle(response.data);
      }
    } catch (err) {
      enqueueSnackbar('Failed to load article', { variant: 'error' });
    }
  };

  const handleDeleteArticle = async (article) => {
    if (!window.confirm(`Are you sure you want to delete "${article.title}"?`)) return;

    try {
      const response = await articleService.delete(article.id);
      if (response.success) {
        enqueueSnackbar('Article deleted successfully', { variant: 'success' });
        loadData();
      }
    } catch (err) {
      enqueueSnackbar('Failed to delete article', { variant: 'error' });
    }
  };

  const handleFeedback = async (articleId, isHelpful) => {
    try {
      const response = await articleService.recordFeedback(articleId, { is_helpful: isHelpful });
      if (response.success) {
        enqueueSnackbar('Thank you for your feedback!', { variant: 'success' });
        if (viewArticle && viewArticle.id === articleId) {
          setViewArticle(prev => ({
            ...prev,
            helpful_count: response.data.helpful_count,
            not_helpful_count: response.data.not_helpful_count,
            helpfulness_score: response.data.helpfulness_score
          }));
        }
      }
    } catch (err) {
      enqueueSnackbar('Failed to record feedback', { variant: 'error' });
    }
  };

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: 10, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view the knowledge base.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/helpdesk`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Knowledge Base
            </Typography>
            <Typography variant="body2" color="text.secondary">
              FAQs and How-to Articles
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <IconButton onClick={loadData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          {canModify && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenForm()}
            >
              New Article
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {ArticleStatusOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Visibility</InputLabel>
              <Select
                value={visibilityFilter}
                label="Visibility"
                onChange={(e) => setVisibilityFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {ArticleVisibilityOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* Categories Sidebar */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Categories
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <List dense>
              <ListItemButton
                selected={!selectedCategory}
                onClick={() => setSelectedCategory('')}
              >
                <ListItemText primary="All Articles" secondary={`${articles.length} articles`} />
              </ListItemButton>
              {categories.map(cat => (
                <ListItemButton
                  key={cat.id}
                  selected={selectedCategory === cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  <ListItemText
                    primary={cat.name}
                    secondary={`${cat.article_count || 0} articles`}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Articles Grid */}
        <Grid item xs={12} md={9}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : articles.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <ArticleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No articles found
              </Typography>
              {canModify && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenForm()}
                >
                  Create First Article
                </Button>
              )}
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {articles.map(article => (
                <Grid item xs={12} sm={6} key={article.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: '0.2s',
                      '&:hover': { boxShadow: 4 }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Chip
                          size="small"
                          label={ArticleStatusLabels[article.status]}
                          color={ArticleStatusColors[article.status]}
                        />
                        <Chip
                          size="small"
                          label={ArticleVisibilityLabels[article.visibility]}
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="h6" gutterBottom sx={{ cursor: 'pointer' }} onClick={() => handleViewArticle(article)}>
                        {article.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {truncateText(article.summary || '', 120)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {article.category?.name || 'Uncategorized'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {article.view_count || 0} views
                        </Typography>
                        {article.helpfulness_score !== null && (
                          <Typography variant="caption" color="text.secondary">
                            {article.helpfulness_score}% helpful
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'space-between' }}>
                      <Button size="small" startIcon={<ViewIcon />} onClick={() => handleViewArticle(article)}>
                        View
                      </Button>
                      {canModify && (
                        <Box>
                          <IconButton size="small" onClick={() => handleOpenForm(article)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteArticle(article)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>

      {/* Article View Dialog */}
      {viewArticle && (
        <Paper
          sx={{
            position: 'fixed',
            top: 80,
            right: 0,
            width: { xs: '100%', md: '60%' },
            height: 'calc(100vh - 80px)',
            overflow: 'auto',
            boxShadow: 8,
            zIndex: 1200,
            p: 3
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Chip
                  size="small"
                  label={ArticleStatusLabels[viewArticle.status]}
                  color={ArticleStatusColors[viewArticle.status]}
                />
                <Chip
                  size="small"
                  label={ArticleVisibilityLabels[viewArticle.visibility]}
                  variant="outlined"
                />
              </Box>
              <Typography variant="h4" gutterBottom>
                {viewArticle.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {viewArticle.category?.name || 'Uncategorized'} • {viewArticle.view_count} views • Updated {formatDate(viewArticle.updated_at)}
              </Typography>
            </Box>
            <Button onClick={() => setViewArticle(null)}>Close</Button>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              '& p': { mb: 2 },
              '& h1, & h2, & h3': { mt: 3, mb: 1 }
            }}
          >
            {viewArticle.content}
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">Was this article helpful?</Typography>
            <Button
              size="small"
              startIcon={<ThumbUpIcon />}
              onClick={() => handleFeedback(viewArticle.id, true)}
            >
              Yes ({viewArticle.helpful_count || 0})
            </Button>
            <Button
              size="small"
              startIcon={<ThumbDownIcon />}
              onClick={() => handleFeedback(viewArticle.id, false)}
            >
              No ({viewArticle.not_helpful_count || 0})
            </Button>
          </Box>
        </Paper>
      )}

      {/* Article Form Dialog */}
      <ArticleForm
        open={formOpen}
        onClose={handleCloseForm}
        article={editArticle}
        chatbotId={chatbotId}
        categories={categories}
      />
    </Box>
  );
};

export default KnowledgeBase;
